import { numToAlpMonth, toExactSubstr } from '../../utils/utilityFunctions';
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { monthwiseCommitmentAmountType } from '../../redux-store/types/api-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { monthwiseCommitmentAmount } from '../../redux-store/actions/application';
import { Card, CardMedia, Grid, Skeleton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { RootStateType } from '../../redux-store/reducers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

ChartJS.register(...registerables);

export const monthWiseCommit: monthwiseCommitmentAmountType = {
  data: [
    {
      month: '',
      completed: 0,
      contributionAmount: 0,
      commitmentAmount: 0,
    },
  ],
  year: '',
};

type CommitmentContributionChartRow = {
  label: string;
  commitment: number;
  contribution: number;
};

const formatCurrencyGraph = (value?: number | null): number => {
  const formats = ['K', 'L', 'CR'];
  let amount: any = Number(value);
  formats.some((key, i) => {
    const size = Math.pow(10, i === 0 ? 3 : 3 - 1);
    if (size <= amount) {
      amount = toExactSubstr(`${amount / size}`);
      if (amount >= 100 && i < formats.length - 1) {
        i++;
      } else {
        amount =
          formats[i] === 'L'
            ? amount.toString().length === 1
              ? Number('0.0' + amount)
              : Number('0.' + amount)
            : amount;
        amount =
          formats[i] === 'K'
            ? amount.toString().length === 1
              ? Number('0.000' + amount)
              : Number('0.00' + amount)
            : amount;
        return true;
      }
    } else {
      return true;
    }
  });
  return Number(amount) || 0;
};

const formatLegendAmount = (value: number): string => {
  const formatted = formatCurrencyGraph(value);
  return `${formatted} Cr`;
};

export default function CommitmentAndContribution({
  fullWidth = false,
}: {
  fullWidth?: boolean;
}): JSX.Element {
  const dispatch = useDispatch();
  const [commitAmtData, SetCommitAmtData] = useState(monthWiseCommit);
  const CommitmentAndContributionData = useSelector(
    (store: RootStateType) => store.commitmentAndContributionData
  );

  const filterData = (CommitmentAndContributionData.data || []).filter((a) => a.completed);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        (await dispatch(monthwiseCommitmentAmount())) as unknown as monthwiseCommitmentAmountType;
        if (!componentIsAlive) {
          return;
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, []);

  useEffect(() => {
    SetCommitAmtData(CommitmentAndContributionData);
  }, [CommitmentAndContributionData]);

  const CommAmtmonth: string[] = [];
  const CommAmtmonthArr = (CommitmentAndContributionData.data || []).map((data) => data.month);
  numToAlpMonth(CommAmtmonthArr, CommAmtmonth);

  const CommitmentamountBar = (CommitmentAndContributionData.data || []).map(
    (amt) => amt.commitmentAmount
  );
  const ContributionamountBar = (CommitmentAndContributionData.data || []).map(
    (amt) => amt.contributionAmount
  );

  const chartRows: CommitmentContributionChartRow[] = CommAmtmonth.map(
    (label: string, index: number): CommitmentContributionChartRow => ({
      label,
      commitment: CommitmentamountBar[index] || 0,
      contribution: ContributionamountBar[index] || 0,
    })
  );

  const CommitmentamountNum = CommitmentamountBar.map((a) => formatCurrencyGraph(a));
  const ContributionamountNum = ContributionamountBar.map((a) => formatCurrencyGraph(a));

  const totalCommitment = CommitmentamountBar.reduce((sum, val) => sum + (val || 0), 0);
  const totalContribution = ContributionamountBar.reduce((sum, val) => sum + (val || 0), 0);

  const chartData = {
    labels: CommAmtmonth,
    datasets: [
      {
        type: 'bar' as const,
        label: 'Commitment Amount',
        data: CommitmentamountNum,
        backgroundColor: '#339385',
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.6,
      },
      {
        type: 'bar' as const,
        label: 'Contribution Amount',
        data: ContributionamountNum,
        backgroundColor: '#FFBC33',
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.6,
      },
    ],
  } as ChartData;

  const chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        yAlign: 'bottom',
        displayColors: true,
        backgroundColor: '#F8F8F8',
        titleColor: '#1A2B3C',
        bodyColor: '#1A2B3C',
        callbacks: {
          label: (item: any) => `  ${item.raw} Crore`,
          labelTextColor: () => 'black',
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#7A8A9A',
          font: { size: 12 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [2, 2],
          color: '#E8ECF0',
        },
        ticks: {
          color: '#7A8A9A',
          font: { size: 12 },
        },
      },
    },
  };

  const isDataReady =
    CommitmentAndContributionData.year &&
    JSON.stringify(commitAmtData) === JSON.stringify(CommitmentAndContributionData);

  return (
    <Grid item xs={12} lg={fullWidth ? 12 : 7} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 320, sm: 420 },
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid #E8ECF0',
          background: '#fff',
          fontFamily: 'Roboto, sans-serif',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          '& .MuiTypography-root': {
            fontFamily: 'Roboto, sans-serif',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 3,
          }}>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontStyle: 'normal',
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: 0,
              color: '#111927',
            }}>
            Commitment vs Contribution Trends
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#7A8A9A', flexShrink: 0 }}>
            Last 12 months
          </Typography>
        </Box>
        {!isDataReady ? (
          <>
            <Skeleton variant="rectangular" sx={{ width: '100%', height: 280, borderRadius: 2 }} />
            <Skeleton sx={{ width: '60%', mt: 2 }} />
          </>
        ) : filterData.length > 0 ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
                mb: 2,
                alignItems: 'flex-start',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 20,
                    py: '4px',
                    boxSizing: 'border-box',
                    opacity: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transform: 'rotate(0deg)',
                  }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#339385',
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#5A6A7A',
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}>
                    Commitment Amount
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontStyle: 'normal',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: 1,
                      letterSpacing: '0.02em',
                      verticalAlign: 'middle',
                      color: '#111927',
                    }}>
                    {formatLegendAmount(totalCommitment)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <Box
                  sx={{
                    width: 12,
                    height: 20,
                    py: '4px',
                    boxSizing: 'border-box',
                    opacity: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transform: 'rotate(0deg)',
                  }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#FFBC33',
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#5A6A7A',
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}>
                    Contribution Amount
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontStyle: 'normal',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: 1,
                      letterSpacing: '0.02em',
                      verticalAlign: 'middle',
                      color: '#111927',
                    }}>
                    {formatLegendAmount(totalContribution)}
                  </Typography>
                </Box>
              </Box>
              {/* {CommitmentAndContributionData.year && (
                <Typography sx={{ fontSize: 12, color: '#7A8A9A', ml: 'auto', alignSelf: 'center' }}>
                  Year {CommitmentAndContributionData.year}
                </Typography>
              )} */}
            </Box>

            <Typography
              component="div"
              className="canvas-container"
              role="region"
              aria-label="Commitment and contribution chart"
              sx={{
                '&.canvas-container': {
                  position: 'relative',
                  width: '100%',
                  height: 300,
                },
              }}>
              <Chart type="bar" data={chartData} options={chartOptions} />
              <Box
                component="table"
                sx={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  p: 0,
                  m: -1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}>
                <caption>Commitment and contribution monthly values</caption>
                <thead>
                  <tr>
                    <th scope="col">Month</th>
                    <th scope="col">Commitment Amount</th>
                    <th scope="col">Contribution Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {chartRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      <td>{row.commitment}</td>
                      <td>{row.contribution}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1.5 }}>
              <InfoOutlinedIcon color="info" fontSize="small" />
              <Typography
                sx={{
                  color: 'rgba(41, 49, 57, 0.7)',
                  fontSize: 11,
                  fontWeight: 500,
                  ml: 1,
                }}>
                Graphical Data is shown only for completed applications.
              </Typography>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              height: 300,
              width: '100%',
            }}>
            <CardMedia
              component="img"
              src="/images/Nodata.png"
              alt="No data available illustration"
              sx={{ height: '40%', width: 'auto', maxWidth: 180, mb: 2 }}
            />
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#7A8A9A' }}>
              No Data Available
            </Typography>
          </Box>
        )}
      </Card>
    </Grid>
  );
}
